import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Stack, Typography } from '@mui/material';
import type { SxProps } from '@mui/system';
import { Control, ControllerRenderProps, FieldError } from 'react-hook-form';
import { slugify } from '../../src/utils/slugify';
import { FieldOption } from '../types/FormField';
import { OptionalTextField } from './OptionalTextField.tsx';

interface CheckboxSetFieldProps {
  label: string;
  disabled: boolean;
  value: string[];
  onChange: ControllerRenderProps['onChange'];
  error?: FieldError;
  name: string;
  readOnly?: boolean;
  options: FieldOption[];
  control: Control;
  direction?: 'horizontal' | 'vertical';
  itemWrapperProps?: {
    sx?: SxProps;
    [key: string]: unknown;
  };
}

function handleCheckboxChange(currentValues: CheckboxSetFieldProps['value'], optionValue: string, onChange: CheckboxSetFieldProps['onChange']): void {
  const updatedValues = currentValues.includes(optionValue) ? currentValues.filter((value) => value !== optionValue) : [...currentValues, optionValue];

  onChange(updatedValues);
}

export const CheckboxSetField = ({
  label,
  disabled,
  value: values,
  onChange,
  error,
  name,
  readOnly,
  options,
  control,
  direction = 'horizontal',
  itemWrapperProps = {},
}: CheckboxSetFieldProps) => {
  return (
    <>
      <FormControl fullWidth size="small" sx={{ pointerEvents: readOnly ? 'none' : 'auto' }}>
        <FormLabel id={`${name}-label`}>{label}</FormLabel>

        <FormGroup>
          <Stack direction={direction === 'horizontal' ? 'row' : 'column'} flexWrap="wrap" columnGap={3} rowGap={2}>
            {options.map(({ value: optionValue, label: optionLabel, textField }) => {
              const valueSlug = slugify(optionValue);
              const { sx = {}, ...otherProps } = itemWrapperProps;

              return (
                <Box key={`${name}-checkbox-${valueSlug}`} sx={{ display: 'flex', alignItems: 'flex-start', ...sx }} {...otherProps}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value={optionValue}
                        disabled={disabled || readOnly}
                        checked={values.includes(optionValue)}
                        onChange={() => handleCheckboxChange(values, optionValue, onChange)}
                      />
                    }
                    label={optionLabel || optionValue}
                  />
                  {textField && (
                    <OptionalTextField
                      dependsOn={{
                        disabled: Boolean(disabled || readOnly),
                        name: name,
                        value: optionValue,
                      }}
                      control={control}
                      textField={textField}
                      key={`${name}-${valueSlug}-additional`}
                    />
                  )}
                </Box>
              );
            })}
            <Box flexGrow="1" />
          </Stack>
        </FormGroup>
      </FormControl>
      {error && (
        <Typography variant="caption" color="error">
          {error.message as string}
        </Typography>
      )}
    </>
  );
};
